// src/components/maps/MapNationalitesCard.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import L, { LatLngBoundsExpression } from 'leaflet';
import { api } from '../../services/api';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import countries from 'world-countries';
import ReactCountryFlag from "react-country-flag";



type Item = { label: string; iso: string; count: number };
type ApiRes = { total: number; items: Item[] };

type Props = {
  className?: string;
  height?: number;
  maxItems?: number; // nombre visible par défaut (ex: 6)
};

// Map ISO2 -> [lat, lng]
const ISO_TO_COORDS: Record<string, [number, number]> = Object.fromEntries(
  countries
    .filter(c => Array.isArray(c.latlng) && c.latlng.length === 2 && c.cca2)
    .map(c => [c.cca2.toUpperCase(), [c.latlng[0], c.latlng[1]] as [number, number]])
);

function isoToFlagEmoji(iso?: string) {
  const s = (iso || '').toUpperCase().trim();
  if (!s || s.length !== 2) return '🏳️';
  const A = 0x1F1E6;
  const code = (c: string) => A + (c.charCodeAt(0) - 65);
  return String.fromCodePoint(code(s[0]), code(s[1]));
}

function FitToData({ coords }: { coords: [number, number][] }) {
  const map = useMap();
  const firstFitDone = useRef(false);

  useEffect(() => {
    if (!coords.length || firstFitDone.current) return;
    const bounds = L.latLngBounds(coords.map(([lat, lng]) => L.latLng(lat, lng))) as any as LatLngBoundsExpression;
    map.fitBounds(bounds, { padding: [30, 30] });
    firstFitDone.current = true;
  }, [coords, map]);

  return null;
}

export default function MapNationalitesCard({
  className = '',
  height = 360,
  maxItems = 6,
}: Props) {
  const [data, setData] = useState<ApiRes | null>(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setErr('');
        const res = await api.get<ApiRes>('/nationalites'); // sans date
        if (!cancelled) setData(res.data);
      } catch (e: any) {
        if (!cancelled) setErr(e?.response?.data?.error || 'Erreur de chargement');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const points = useMemo(() => {
    if (!data) return [];
    const max = Math.max(...data.items.map(i => i.count), 1);
    return data.items
      .map(i => {
        const iso = (i.iso || '').toUpperCase().trim();
        // si iso vide, on peut tenter de ne pas afficher ce point (ou essayer un fallback par nom, voir plus bas)
        const coord = ISO_TO_COORDS[iso];
        if (!coord) return null;
        const r = 6 + Math.round((i.count / max) * 14);
        return { ...i, coord, r };
      })
      .filter(Boolean) as Array<Item & { coord: [number, number]; r: number }>;
  }, [data]);

  const coords = points.map(p => p.coord);

  // liste complète + % + drapeau
  const listAll = useMemo(() => {
    if (!data) return [];
      console.log("DATA /nationalites", data.items); // 🔴 Debug ici
    const total = Math.max(1, data.total || 0);
    return [...data.items]
      .sort((a, b) => b.count - a.count)
      .map(it => ({
        ...it,
        pct: Math.round((it.count / total) * 100),
        flag: isoToFlagEmoji(it.iso),
      }));
  }, [data]);

  // nouveau calcul pour garder le bouton visible même quand tout est affiché
  const totalCount = listAll.length;
  const hasMore = totalCount > maxItems;
  const visibleList = showAll ? listAll : listAll.slice(0, maxItems);
  const hiddenCount = Math.max(0, totalCount - maxItems);

  // Observes a DOM element and invalidates the Leaflet map whenever that element resizes
  function MapAutoSizer({ containerRef }: { containerRef: React.RefObject<HTMLElement> }) { 
    const map = useMap();

    useEffect(() => {
      // 1) invalidate once when the map is ready
      const t = setTimeout(() => map.invalidateSize(), 0);

      // 2) observe wrapper element size changes (works for breakpoint changes too)
      const el = containerRef.current;
      if (!el) return () => clearTimeout(t); 

      const ro = new ResizeObserver(() => {
        map.invalidateSize(false);
      });
      ro.observe(el);

      // 3) fallback: also react to window resize
      const onWinResize = () => map.invalidateSize(false);
      window.addEventListener('resize', onWinResize);

      return () => {
        clearTimeout(t);
        ro.disconnect();
        window.removeEventListener('resize', onWinResize);
      };
    }, [containerRef, map]);

    return null;
  }

  const wrapperRef = useRef<HTMLDivElement>(null);

  return (
    <div className={`rounded-2xl border border-gray-200 bg-white p-5 dark:border-darkBorderCard dark:bg-white/[0.03] sm:p-6 ${className}`}>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">Nationalités des clients</h3>
          {data && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Nombre de clients : {data.total}
            </p>
          )}
        </div>
      </div>

      {loading && <div className="text-gray-500">Chargement…</div>}
      {err && !loading && <div className="text-red-600">{err}</div>}

      {!loading && !err && (
        <>
          {/* Carte */}
          <div style={{ height }} className="mb-6 overflow-hidden rounded-xl">
            <MapContainer
              style={{ height: '100%', width: '100%' }}
              center={[46, 2]}
              zoom={3}
              worldCopyJump
              minZoom={2}
              maxZoom={8}
              scrollWheelZoom
            >
              
               <MapAutoSizer containerRef={wrapperRef as React.RefObject<HTMLElement>} />
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution="&copy; OpenStreetMap contributors"
              />
              <FitToData coords={coords} />
              {points.map((p, idx) => (
                <CircleMarker
                  key={idx}
                  center={p.coord}
                  radius={p.r}
                  pathOptions={{ color: '#4f46e5', fillColor: '#4f46e5', fillOpacity: 0.25, weight: 2 }}
                >
                  <Tooltip direction="top" offset={[0, -4]} opacity={1}>
                    <div className="text-xs">
                      <div className="font-semibold">{p.label || p.iso || 'Inconnu'}</div>
                      <div>{p.count} client(s)</div>
                    </div>
                  </Tooltip>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>

          {/* Liste + bouton */}
          <div className="space-y-4">
            {visibleList.map((it, i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  
                  <ReactCountryFlag countryCode={it.iso} svg style={{ width: '1.5em', height: '1.5em' }} />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-800 dark:text-white/90 truncate">
                      {it.label || 'Inconnu'}
                    </p>
                    <span className="block text-xs text-gray-500 dark:text-gray-400">
                      {it.count} client{it.count > 1 ? 's' : ''}
                    </span>
                  </div>
                </div>

                <div className="flex w-full max-w-[180px] items-center gap-3">
                  <div className="relative block h-2 w-full rounded-sm bg-gray-200 dark:bg-gray-800">
                    <div
                      className="absolute left-0 top-0 h-full rounded-sm bg-indigo-600"
                      style={{ width: `${it.pct}%` }}
                    />
                  </div>
                  <p className="text-sm font-medium text-gray-800 dark:text-white/90 tabular-nums">
                    {it.pct}%
                  </p>
                </div>
              </div>
            ))}

            {hasMore && (
              <div className="pt-2">
                <button
                  type="button"
                  onClick={() => setShowAll(s => !s)}
                  className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-white/5"
                  aria-expanded={showAll}
                >
                  {showAll ? (
                    <>
                      Voir moins <ChevronUpIcon className="h-4 w-4" />
                    </>
                  ) : (
                    <>
                      Afficher {hiddenCount} pays de plus <ChevronDownIcon className="h-4 w-4" />
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </>
      )}
       <style>
      {`
        .leaflet-container {
          z-index: 0 !important;
        }
        .leaflet-pane,
        .leaflet-top,
        .leaflet-bottom {
          z-index: 1 !important;
        }
      `}
    </style>
    </div>
  );
}
